import { useState, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Inbox as InboxIcon,
  Mail,
  ArrowDownAZ,
  Flame,
  Plus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { NewDraftComposer } from '@/components/drafts/NewDraftComposer';
import { DraftComposer } from '@/components/drafts/DraftComposer';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { bundleMessages, sectionRows, type InboxRow } from '@/lib/inbox';
import { MessageRow } from '@/components/inbox/MessageRow';
import { BundleRow } from '@/components/inbox/BundleRow';
import { MessagePanel } from '@/components/inbox/MessagePanel';
import type { EmailClassification } from '@cc/types';

type SortMode = 'urgency' | 'recent';

interface QuickFilter {
  key: string;
  label: string;
  tags?: string[];
  classifications?: EmailClassification[];
}

const QUICK_FILTERS: QuickFilter[] = [
  { key: 'reply', label: 'Reply', classifications: ['IMPORTANT_REPLY'] },
  { key: 'invoice', label: 'Invoices', tags: ['invoice'] },
  { key: 'receipt', label: 'Receipts', tags: ['receipt'] },
  { key: 'meeting', label: 'Meetings', tags: ['meeting-invite', 'meeting-reschedule'] },
  { key: 'family', label: 'Family', tags: ['family'] },
  { key: 'customer', label: 'Customer', tags: ['customer-issue'] },
];

export function InboxPage() {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedClassifications, setSelectedClassifications] = useState<EmailClassification[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortMode>('urgency');
  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [composedDraftId, setComposedDraftId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    reply: true,
    activity: true,
    pending: true,
    cleanup: false,
  });

  const accountsQuery = useQuery({
    queryKey: qk.accounts,
    queryFn: () => api.listAccounts(),
  });

  const filters = useMemo(
    () => ({
      accounts: selectedAccounts,
      classifications: selectedClassifications,
      tags: selectedTags,
      search: search.trim() || undefined,
      sort,
    }),
    [selectedAccounts, selectedClassifications, selectedTags, search, sort],
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: qk.messages(filters),
    queryFn: ({ pageParam }) =>
      api.listMessages({ ...filters, cursor: pageParam as string | undefined }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  const allMessages = messagesQuery.data?.pages.flatMap((p) => p.messages) ?? [];
  const sectioned = useMemo(() => {
    const rows = bundleMessages(allMessages);
    return sectionRows(rows);
  }, [allMessages]);

  function applyQuickFilter(key: string) {
    const filter = QUICK_FILTERS.find((f) => f.key === key);
    if (!filter) return;
    if (activeQuickFilter === key) {
      setActiveQuickFilter(null);
      setSelectedClassifications([]);
      setSelectedTags([]);
    } else {
      setActiveQuickFilter(key);
      setSelectedClassifications(filter.classifications ?? []);
      setSelectedTags(filter.tags ?? []);
    }
  }

  function selectMessage(id: string) {
    setSelected(id);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight md:text-2xl">Inbox</h1>
          <p className="text-xs text-muted-foreground md:text-sm">
            Unified across all connected accounts. Prioritised by urgency by default.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SortToggle value={sort} onChange={setSort} />
          <Button
            size="sm"
            onClick={() => {
              setComposing(true);
              setComposedDraftId(null);
            }}
          >
            <Plus className="h-4 w-4" /> New email
          </Button>
        </div>
      </div>

      {composing && !composedDraftId && (
        <NewDraftComposer
          onCancel={() => setComposing(false)}
          onCreated={(id) => setComposedDraftId(id)}
        />
      )}
      {composedDraftId && (
        <div className="rounded-md border-2 border-foreground/30 bg-muted/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
              New email
            </p>
            <Link
              to={`/drafts?id=${composedDraftId}`}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Open in Drafts
            </Link>
          </div>
          <DraftComposer
            id={composedDraftId}
            compact
            showOriginal={false}
            onSent={() => {
              setComposing(false);
              setComposedDraftId(null);
            }}
            onDiscarded={() => {
              setComposing(false);
              setComposedDraftId(null);
            }}
          />
        </div>
      )}

      <Card>
        <CardContent className="space-y-3 p-3">
          <div className="flex items-center gap-2 px-1">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search subjects, snippets, senders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 border-0 px-0 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 border-t border-border pt-2.5">
            <span className="inline-flex items-center gap-1 pl-1 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Filter className="h-3 w-3" />
            </span>
            {QUICK_FILTERS.map((f) => (
              <FilterChip
                key={f.key}
                label={f.label}
                active={activeQuickFilter === f.key}
                onClick={() => applyQuickFilter(f.key)}
              />
            ))}
            {accountsQuery.data?.accounts.length ? (
              <>
                <span className="mx-1 text-muted-foreground/40">·</span>
                {accountsQuery.data.accounts.map((a) => (
                  <FilterChip
                    key={a.id}
                    label={a.email.split('@')[0] ?? a.email}
                    active={selectedAccounts.includes(a.id)}
                    onClick={() =>
                      setSelectedAccounts((prev) =>
                        prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id],
                      )
                    }
                  />
                ))}
              </>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {messagesQuery.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : allMessages.length === 0 ? (
        <EmptyState hasAccounts={(accountsQuery.data?.accounts.length ?? 0) > 0} />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.02 } } }}
            className={cn(
              'space-y-5',
              selected ? 'hidden lg:block lg:col-span-2' : 'lg:col-span-5',
            )}
          >
            <InboxSection
              title="Needs reply"
              tone="success"
              rows={sectioned.reply}
              selectedId={selected}
              onSelect={selectMessage}
              expanded={expandedSections.reply!}
              onToggle={() => setExpandedSections((s) => ({ ...s, reply: !s.reply }))}
            />
            <InboxSection
              title="Activity"
              tone="default"
              rows={sectioned.activity}
              selectedId={selected}
              onSelect={selectMessage}
              expanded={expandedSections.activity!}
              onToggle={() => setExpandedSections((s) => ({ ...s, activity: !s.activity }))}
            />
            <InboxSection
              title="Pending triage"
              tone="warning"
              rows={sectioned.pending}
              selectedId={selected}
              onSelect={selectMessage}
              expanded={expandedSections.pending!}
              onToggle={() => setExpandedSections((s) => ({ ...s, pending: !s.pending }))}
            />
            <InboxSection
              title="Cleanup pile"
              tone="muted"
              rows={sectioned.cleanup}
              selectedId={selected}
              onSelect={selectMessage}
              expanded={expandedSections.cleanup!}
              onToggle={() => setExpandedSections((s) => ({ ...s, cleanup: !s.cleanup }))}
            />
            {messagesQuery.hasNextPage && sort === 'recent' && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => void messagesQuery.fetchNextPage()}
                disabled={messagesQuery.isFetchingNextPage}
              >
                {messagesQuery.isFetchingNextPage ? 'Loading...' : 'Load more'} <ChevronDown className="h-3 w-3" />
              </Button>
            )}
          </motion.div>
          {selected && (
            <div className="lg:col-span-3">
              <div className="mb-2 lg:hidden">
                <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>
                  <ChevronLeft className="h-3.5 w-3.5" /> Back to inbox
                </Button>
              </div>
              <MessagePanel id={selected} onClose={() => setSelected(null)} onSelectMessage={selectMessage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SortToggle({ value, onChange }: { value: SortMode; onChange: (v: SortMode) => void }) {
  return (
    <div className="inline-flex rounded-md border border-border bg-card p-0.5">
      <button
        onClick={() => onChange('urgency')}
        className={cn(
          'inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs transition-colors',
          value === 'urgency' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <Flame className="h-3 w-3" /> Urgency
      </button>
      <button
        onClick={() => onChange('recent')}
        className={cn(
          'inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs transition-colors',
          value === 'recent' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <ArrowDownAZ className="h-3 w-3" /> Recent
      </button>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-md border px-2 py-0.5 text-[11px] transition-colors',
        active
          ? 'border-foreground bg-foreground text-background'
          : 'border-border text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function InboxSection({
  title,
  tone,
  rows,
  selectedId,
  onSelect,
  expanded,
  onToggle,
}: {
  title: string;
  tone: 'success' | 'default' | 'warning' | 'muted';
  rows: InboxRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  if (rows.length === 0) return null;
  const dotColor =
    tone === 'success'
      ? 'bg-emerald-500'
      : tone === 'warning'
        ? 'bg-amber-500'
        : tone === 'muted'
          ? 'bg-zinc-500'
          : 'bg-zinc-300';

  return (
    <section className="space-y-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="group flex w-full items-center gap-2 px-1 py-1"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className={cn('h-1.5 w-1.5 rounded-full', dotColor)} />
        <span className="text-xs font-medium uppercase tracking-wider text-foreground/80">
          {title}
        </span>
        <span className="text-[11px] text-muted-foreground">({countMessages(rows)})</span>
      </button>
      {expanded && (
        <div className="space-y-1.5">
          {rows.map((row) =>
            row.kind === 'message' ? (
              <MessageRow
                key={row.message.id}
                message={row.message}
                selected={selectedId === row.message.id}
                onSelect={() => onSelect(row.message.id)}
                threadCount={row.threadCount}
              />
            ) : (
              <BundleRow
                key={row.key}
                bundle={row}
                selectedId={selectedId}
                onSelectMessage={onSelect}
              />
            ),
          )}
        </div>
      )}
    </section>
  );
}

function countMessages(rows: InboxRow[]): number {
  return rows.reduce((acc, r) => acc + (r.kind === 'bundle' ? r.messages.length : 1), 0);
}

function EmptyState({ hasAccounts }: { hasAccounts: boolean }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
          {hasAccounts ? <Mail className="h-5 w-5" /> : <InboxIcon className="h-5 w-5" />}
        </div>
        {hasAccounts ? (
          <>
            <p className="text-sm">No messages match these filters.</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Try clearing the quick filters or the search. New messages sync every 5 minutes.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm">No Gmail accounts connected yet.</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Head to Settings → Email accounts and connect at least one account. Once OAuth is
              granted the worker starts syncing within 5 minutes.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
